import { BLOCK_LABELS, ParcelResearch, ResearchBlock, ResearchSetup } from "./types";

const List = ({ title, values }: { title: string; values?: string[] }) =>
  values?.length ? (
    <section>
      <h3 className="font-bold">{title}</h3>
      <ul className="list-disc space-y-1 pl-5">
        {values.map((value, index) => (
          // Tekst uit een afgerond onderzoek: vast, en kan dubbel voorkomen
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <li key={index}>{value}</li>
        ))}
      </ul>
    </section>
  ) : null;

// Een beperkt onderzoek mag niet voor een volledig doorgaan: zeg ook wat er
// buiten bleef.
const SetupSummary = ({ setup }: { setup: ResearchSetup }) => {
  const leftOutBlocks = (Object.keys(BLOCK_LABELS) as ResearchBlock[])
    .filter((block) => !setup.blocks.includes(block))
    .map((block) => BLOCK_LABELS[block].toLowerCase());
  return (
    <section className="mt-4 rounded bg-gray-50 p-3 text-xs text-gray-700">
      <h3 className="font-bold">Onderzoeksopzet</h3>
      {setup.mode === "question" ? (
        <p className="mt-1">
          Eigen vraag ({setup.depth === "concise" ? "beknopt" : "uitgebreid"}):{" "}
          <em>{setup.question}</em>
        </p>
      ) : (
        <>
          <p className="mt-1">Onderzochte onderwerpen: {setup.topics.join(", ")}.</p>
          {setup.leftOutTopics.length > 0 && (
            <p className="mt-1">Buiten beschouwing: {setup.leftOutTopics.join(", ")}.</p>
          )}
        </>
      )}
      {leftOutBlocks.length > 0 && (
        <p className="mt-1 text-gray-500">Niet gevraagd: {leftOutBlocks.join(", ")}.</p>
      )}
    </section>
  );
};

export const ResearchDetail = ({
  research,
  onCancel,
  onRetry,
  onRerun,
  onSourceAction,
}: {
  research: ParcelResearch;
  onCancel: () => void;
  onRetry: () => void;
  onRerun: () => void;
  onSourceAction: (sourceId: number, action: string) => void;
}) => {
  const report = research.report || {};
  const sourcesByCode = new Map(research.sources.map((source) => [source.code, source]));
  const active = research.status === "queued" || research.status === "running";
  return (
    <article className="h-full overflow-y-auto p-5 text-sm">
      <div className="flex items-start justify-between gap-3 border-b pb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Kavelonderzoek</p>
          <h2 className="text-xl font-bold">
            {research.targets.map((target) => target.parcelName).join(", ")}
          </h2>
          <p className="text-xs text-gray-500">
            {new Date(research.created).toLocaleString("nl-NL")} ·{" "}
            {research.progressDetail || research.status}
          </p>
        </div>
        <div className="flex gap-2">
          {active && (
            <button onClick={onCancel} className="rounded border px-3 py-1.5 text-xs">
              Annuleren
            </button>
          )}
          {research.status === "failed" && (
            <button
              onClick={onRetry}
              className="rounded bg-bright-yellow px-3 py-1.5 text-xs font-bold"
            >
              Opnieuw proberen
            </button>
          )}
          {research.status === "completed" && (
            <button onClick={onRerun} className="rounded border px-3 py-1.5 text-xs">
              Opnieuw onderzoeken
            </button>
          )}
        </div>
      </div>

      {research.setup && <SetupSummary setup={research.setup} />}

      {research.errorMessage && (
        <p className="mt-4 rounded bg-red-50 p-3 text-red-700">{research.errorMessage}</p>
      )}
      {active && <p className="mt-6 text-gray-500">Je kunt ondertussen gewoon verder werken.</p>}

      {research.status === "completed" && (
        <div className="mt-5 space-y-5">
          <section>
            <h3 className="font-bold">Samenvatting</h3>
            <p className="mt-1 whitespace-pre-wrap">{report.summary}</p>
          </section>
          <List title="Kansen" values={report.opportunities} />
          <List title="Risico's en beperkingen" values={report.risks} />
          {(report.sections || []).map((section: any, index: number) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- vaste secties van een afgerond onderzoek
            <details key={index} open={index === 0} className="rounded border p-3">
              <summary className="cursor-pointer font-bold">{section.title}</summary>
              <p className="mt-2 whitespace-pre-wrap">{section.content}</p>
              {!!section.source_codes?.length && (
                <p className="mt-2 text-xs text-gray-500">
                  Bronnen:{" "}
                  {section.source_codes.map((code: string, sourceIndex: number) => {
                    const source = sourcesByCode.get(code);
                    return source ? (
                      <span key={code}>
                        {sourceIndex > 0 && ", "}
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          [{code}]
                        </a>
                      </span>
                    ) : null;
                  })}
                </p>
              )}
            </details>
          ))}
          <List title="Onzekerheden" values={report.uncertainties} />
          <List title="Aanbevolen vervolgonderzoek" values={report.recommendations} />
          {research.reportFileUrl && (
            <a
              href={research.reportFileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-blue-600 hover:underline"
            >
              Open PDF-rapport
            </a>
          )}
          {research.previousVersionId && (
            <a
              href={`/workspace/${research.workspaceId}/research/${research.previousVersionId}`}
              className="ml-4 inline-block text-blue-600 hover:underline"
            >
              Bekijk vorige versie
            </a>
          )}
          <section>
            <h3 className="font-bold">Bronnen</h3>
            <ul className="mt-2 space-y-2">
              {research.sources.map((source) => (
                <li key={source.id} className="rounded border p-2">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block break-words font-semibold text-blue-600 hover:underline"
                  >
                    [{source.code}] {source.title || source.publisher || source.url}
                  </a>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                    <span>
                      Geraadpleegd {new Date(source.accessedAt).toLocaleDateString("nl-NL")}
                    </span>
                    {source.official && <span className="text-dark-green">Officiële bron</span>}
                    {source.workspaceLinkState === "proposed" && (
                      <>
                        <button
                          onClick={() => onSourceAction(source.id, "add-link")}
                          className="rounded bg-bright-yellow px-2 py-1"
                        >
                          Voeg toe als projectlink
                        </button>
                        <button
                          onClick={() => onSourceAction(source.id, "dismiss-link")}
                          className="px-2 py-1 text-gray-500"
                        >
                          Negeren
                        </button>
                      </>
                    )}
                    {source.workspaceLinkState === "added" && <span>Toegevoegd aan project</span>}
                    {source.isDocument && source.documentState === "proposed" && (
                      <>
                        <button
                          onClick={() => onSourceAction(source.id, "import-document")}
                          className="rounded border px-2 py-1"
                        >
                          Voeg PDF toe aan project
                        </button>
                        <button
                          onClick={() => onSourceAction(source.id, "dismiss-document")}
                          className="px-2 py-1 text-gray-500"
                        >
                          PDF negeren
                        </button>
                      </>
                    )}
                    {source.documentState === "added" && source.workspaceFileUrl && (
                      <span>
                        PDF toegevoegd:{" "}
                        <a
                          href={source.workspaceFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {source.workspaceFileName || "Open bestand"}
                        </a>
                      </span>
                    )}
                    {source.documentState === "added" && !source.workspaceFileUrl && (
                      <span>PDF toegevoegd</span>
                    )}
                    {source.documentState === "queued" && <span>PDF staat in de wachtrij</span>}
                    {source.documentState === "importing" && <span>PDF wordt gecontroleerd…</span>}
                    {source.documentState === "failed" && (
                      <>
                        <span className="basis-full text-red-600">
                          {source.documentError || "PDF-import mislukt"}
                        </span>
                        <button
                          onClick={() => onSourceAction(source.id, "import-document")}
                          className="rounded border px-2 py-1"
                        >
                          PDF-import opnieuw proberen
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </article>
  );
};
